import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ModalContainerComponent } from '../../../shared/components/modal-container/modal-container.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomerService } from '../../../core/services/customer.service';
import { FacilitiesService } from '../../../core/services/facilities.service';
import { combineLatest, firstValueFrom, map, of, tap } from 'rxjs';
import { IFormChangeEmitter } from '../../../shared/components/form/form.component';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { IUser } from '../../../shared/models/be/user';
import { NotificationService } from '../../../core/services/notification.service';
import { IonButton, IonContent } from "@ionic/angular/standalone";
import { TableService } from '../../../shared/components/table/table.service';
import { SharedService } from '../../../core/services/shared.service';
import { MonitoringPreferencesComponent } from '../../../shared/components/monitoring-preferences/monitoring-preferences.component';
import { MonitoringPreferencesService } from '../../../core/services/monitoring-preferences.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
  imports: [IonContent,
    MonitoringPreferencesComponent,
    CommonModule,
    ModalContainerComponent,
    MatGridListModule,
    MatFormFieldModule,
    TranslateModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    IonButton,
    IonContent
  ]
})
export class SettingsPage implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(MonitoringPreferencesComponent) monitoringChecklist?: MonitoringPreferencesComponent;

  public currentLanguage: string = 'it';

  public formStatus: IFormChangeEmitter = {
    canSaveDraft: true,
    canSaveData: true,
  }

  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'settings',
    routerLink: '',
    label: 'SETTINGS'
  }]

  public settingsData: ISettingsData[] = [];

  constructor(
    private translate: TranslateService,
    private customerService: CustomerService,
    private facilitiesService: FacilitiesService,
    private requestsService: RequestsService,
    private toastService: ToastService,
    private notificationService: NotificationService,
    private sharedService: SharedService,
    private tableService: TableService,
    private router: Router,
    private monitoringPreferences: MonitoringPreferencesService
  ) { }

  private currentUser = this.requestsService.getCurrentUser();

  get monitoringBuildingId(): string | null {
    if (this.currentUser?.role === 'superuser') {
      const inputs = ['customer', 'organization', 'building'].map(id => this.getOrganizationInput(id));
      return inputs.every(input => input?.selectedValue && input.values.some(value => value.id === input.selectedValue))
        ? inputs[2]!.selectedValue! : null;
    }
    return this.settingsData.find(section => section.id === 'building')?.inputs[0]?.selectedValue
      || this.currentUser?.building?.id || null;
  }

  ngOnInit() {
    this.currentLanguage = this.resolveLanguage(this.translate.currentLang || this.currentUser?.language || this.translate.defaultLang);
    this.settingsData.push({
      id: 'language',
      label: 'LANGUAGE',
      rowSpan: 2,
      inputs: [{
        id: 'language',
        type: 'select',
        length: 2,
        label: 'LANGUAGE',
        selectedValue: this.currentLanguage,
        values: [{
          id: 'it',
          label: 'ITALIAN'
        }, {
          id: 'en',
          label: 'ENGLISH'
        }],
        onSelect: (resourceId) => this.changeLanguage(resourceId as string)
      }]
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.currentLanguage = this.resolveLanguage(event.lang);
      const languageInput = this.settingsData.find(section => section.id === 'language')?.inputs[0];
      if (languageInput) languageInput.selectedValue = this.currentLanguage;
    });
    if (this.currentUser?.role === 'superuser') {
      combineLatest([
        this.customerService.loadCustomers$(),
        this.currentUser?.customer?.id ? this.facilitiesService.getOrganization$(this.currentUser.customer.id) : of([]),
        this.currentUser?.organization?.id ? this.facilitiesService.getBuildings$() : of([])
      ]).pipe(
        tap(([customersData, organizations, buildings]) => {
          this.settingsData.push({
            id: 'organization',
            label: 'ORGANIZATION',
            rowSpan: 2,
            inputs: [{
              id: 'customer',
              type: 'select',
              length: 2,
              label: 'CUSTOMER',
              selectedValue: this.currentUser?.customer?.id ? String(this.currentUser.customer.id) : null,
              disableTranslation: true,
              values: customersData.map((customer) => ({
                id: customer.id + "",
                label: customer.businessName,
                spatialTrackingEnabled: customer.spatialTrackingEnabled
              })),
              onSelect: (resourceId) => this.onCustomerSelect(resourceId as string)
            }, {
              id: 'organization',
              type: 'select',
              length: 2,
              selectedValue: this.currentUser?.organization?.id || (organizations.length ? organizations[0].id : null),
              label: 'ORGANIZATION',
              disableTranslation: true,
              values: (organizations || []).map((org) => ({
                id: org.id,
                label: org.name
              })),
              onSelect: (resourceId) => this.onOrganizationSelect(resourceId as string)
            }, {
              id: 'building',
              type: 'select',
              length: 2,
              selectedValue: this.currentUser?.building?.id || (buildings.length === 1 ? buildings[0].id : null),
              label: 'BUILDING',
              disableTranslation: true,
              values: (buildings || []).map((building) => ({
                id: building.id!,
                label: building.name!
              })),
              onSelect: () => this.updateOrganizationResetButtonState()
            }, {
              id: 'resetOrganizationAssociation',
              type: 'button',
              length: 1,
              label: 'RESET_ORGANIZATION_ASSOCIATION',
              values: [],
              fill: 'outline',
              color: 'danger',
              disabled: !(this.currentUser?.customer?.id
                && this.currentUser?.organization?.id
                && this.currentUser?.building?.id),
              onSelect: () => this.resetOrganizationAssociation()
            }]
          });
        })
      ).subscribe();
    } else {
      this.monitoringPreferences.buildings().pipe(map(result => result.buildings),
        tap((buildings) => {
          this.settingsData.push({
            id: 'building',
            label: 'BUILDING_SET',
            rowSpan: 2,
            inputs: [{
              id: 'building',
              type: 'select',
              length: 2,
              selectedValue: this.currentUser?.building?.id || (buildings.length === 1 ? buildings[0].id : null),
              label: 'BUILDING',
              disableTranslation: true,
              values: (buildings || []).map((building) => ({
                id: building.id!,
                label: building.name!
              }))
            }]
          })
        })
      ).subscribe();
    }

  }

  changeLanguage(lang: string) {
    this.translate.use(lang);
  }

  private resolveLanguage(lang?: string): string {
    const language = lang?.toLowerCase().split('-')[0];
    return language === 'en' ? 'en' : 'it';
  }

  private async refreshAssociationOptions(): Promise<void> {
    const customerId = this.getOrganizationInput('customer')?.selectedValue;
    const organizationInput = this.getOrganizationInput('organization');
    const buildingInput = this.getOrganizationInput('building');
    if (!customerId || !organizationInput || !buildingInput) return;
    const organizations = await firstValueFrom(this.facilitiesService.getOrganization$(Number(customerId)));
    organizationInput.values = organizations.map(organization => ({ id: organization.id, label: organization.name }));
    if (!organizationInput.values.some(value => value.id === organizationInput.selectedValue)) {
      organizationInput.selectedValue = organizations.length === 1 ? organizations[0].id : null;
    }
    if (!organizationInput.selectedValue) { buildingInput.values = []; return; }
    const buildings = await firstValueFrom(this.facilitiesService.getBuildings$(organizationInput.selectedValue));
    buildingInput.values = buildings.map(building => ({ id: building.id!, label: building.name! }));
    if (!buildingInput.values.some(value => value.id === buildingInput.selectedValue)) {
      buildingInput.selectedValue = buildings.length === 1 ? buildings[0].id! : null;
    }
    this.updateOrganizationResetButtonState();
  }

  private getOrganizationInput(inputId: string) {
    return this.settingsData
      .find((setting) => setting.id === 'organization')
      ?.inputs.find((input) => input.id === inputId);
  }

  private hasSavedOrganizationAssociation(): boolean {
    const currentUser = this.requestsService.getCurrentUser();
    if (currentUser?.role !== 'superuser'
      || !currentUser.customer?.id
      || !currentUser.organization?.id
      || !currentUser.building?.id) {
      return false;
    }

    return String(this.getOrganizationInput('customer')?.selectedValue) === String(currentUser.customer.id)
      && String(this.getOrganizationInput('organization')?.selectedValue) === String(currentUser.organization.id)
      && String(this.getOrganizationInput('building')?.selectedValue) === String(currentUser.building.id);
  }

  private updateOrganizationResetButtonState(): void {
    const resetButton = this.getOrganizationInput('resetOrganizationAssociation');
    if (!resetButton) return;

    resetButton.disabled = !this.hasSavedOrganizationAssociation();
  }

  private onCustomerSelect(customerId: string): void {
    const organizationInput = this.getOrganizationInput('organization');
    const buildingInput = this.getOrganizationInput('building');
    if (organizationInput) {
      organizationInput.selectedValue = null;
      organizationInput.values = [];
    }
    if (buildingInput) {
      buildingInput.selectedValue = null;
      buildingInput.values = [];
    }
    this.updateOrganizationResetButtonState();
    this.loadOrganizations(Number(customerId));
  }

  private onOrganizationSelect(organizationId: string): void {
    const buildingInput = this.getOrganizationInput('building');
    if (buildingInput) {
      buildingInput.selectedValue = null;
      buildingInput.values = [];
    }
    this.updateOrganizationResetButtonState();
    this.loadBuildings(organizationId);
  }

  loadOrganizations(customerId: number) {
    this.facilitiesService.getOrganization$(customerId).pipe(
      tap((data) => {
        if (String(this.getOrganizationInput('customer')?.selectedValue) !== String(customerId)) return;
        const orgSection = this.settingsData.find((setting) => setting.id === 'organization')?.inputs.find((input) => input.id === 'organization');
        const buildingSection = this.settingsData.find((setting) => setting.id === 'organization')?.inputs.find((input) => input.id === 'building');
        if (buildingSection) buildingSection.values = [];
        if (orgSection) {
          const selectedOrganization = orgSection.selectedValue;
          orgSection.values = data.map((org) => ({
            id: org.id,
            label: org.name
          }));
          orgSection.selectedValue = orgSection.values.some(value => value.id === selectedOrganization) ? selectedOrganization
            : orgSection.values.length === 1 ? orgSection.values[0].id : null;
          if (orgSection.selectedValue) {
            this.loadBuildings(orgSection.selectedValue);
          } else {
            this.updateOrganizationResetButtonState();
          }
        }
      })
    ).subscribe();
  }

  loadBuildings(organizationId: string) {
    const selectedCustomer = this.settingsData.find((setting) => setting.id === 'organization')!.inputs.find((input) => input.id === 'customer')!.selectedValue;
    selectedCustomer && this.facilitiesService.getBuildings$(organizationId).pipe(
      tap((data) => {
        if (this.getOrganizationInput('organization')?.selectedValue !== organizationId
          || this.getOrganizationInput('customer')?.selectedValue !== selectedCustomer) return;
        const buildingSection = this.settingsData.find((setting) => setting.id === 'organization')?.inputs.find((input) => input.id === 'building');
        if (buildingSection) {
          const selectedBuilding = buildingSection.selectedValue;
          buildingSection.values = data.map((building) => ({
            id: building.id!,
            label: building.name!
          }))
          buildingSection.selectedValue = buildingSection.values.some(value => value.id === selectedBuilding) ? selectedBuilding
            : buildingSection.values.length === 1 ? buildingSection.values[0].id : null;
          this.updateOrganizationResetButtonState();
        }
      })
    ).subscribe();
  }

  resetOrganizationAssociation(): void {
    const currentUser = this.requestsService.getCurrentUser();
    if (currentUser?.role !== 'superuser' || !this.hasSavedOrganizationAssociation()) {
      return;
    }

    this.sharedService.stopWss();
    const unassociatedUser: IUser = {
      ...currentUser,
      customer: undefined,
      organization: undefined,
      building: undefined
    };
    this.requestsService.saveCurrentUser(unassociatedUser);
    this.currentUser = unassociatedUser;
    this.clearAlertsHistoryTableFilters();
    this.sharedService.clearAlerts();

    const customerInput = this.getOrganizationInput('customer');
    const organizationInput = this.getOrganizationInput('organization');
    const buildingInput = this.getOrganizationInput('building');
    if (customerInput) customerInput.selectedValue = null;
    if (organizationInput) {
      organizationInput.selectedValue = null;
      organizationInput.values = [];
    }
    if (buildingInput) {
      buildingInput.selectedValue = null;
      buildingInput.values = [];
    }
    this.updateOrganizationResetButtonState();

    this.toastService.showToast(
      this.translate.instant('GENERAL.SUCCESS.ORGANIZATION_ASSOCIATION_RESET'),
      IonicColors.GREEN
    );
    this.router.navigate(['/wita/customers']);
  }

  async submitData() {
    const floorsChanged = this.monitoringChecklist?.dirty;
    if (this.monitoringChecklist && !await this.monitoringChecklist.saveIfNeeded()) return;
    if (floorsChanged && this.currentUser?.role === 'superuser') {
      try { await this.refreshAssociationOptions(); }
      catch {
        this.toastService.showToast(this.translate.instant('GENERAL.ERRORS.SET_ORG'), IonicColors.RED);
        return;
      }
    }
    const currentUser = !!localStorage.getItem('USER') && JSON.parse(localStorage.getItem('USER')!);
    this.settingsData.forEach((setting) => {
      switch (setting.id) {
        case 'organization':
          let orgObject: { [key: string]: { id: string | number, businessName: string, spatialTrackingEnabled?: boolean } } = {};
          setting.inputs.forEach((input) => {
            const selectedValue = input.values.find((value) => value.id === input.selectedValue);
            orgObject[input.id] = {
              id: selectedValue?.id || "",
              businessName: selectedValue?.label || "",
              ...(input.id === 'customer'
                ? { spatialTrackingEnabled: selectedValue?.spatialTrackingEnabled === true }
                : {})
            }
          })
          if (currentUser) {
            this.sharedService.stopWss();
            (currentUser as IUser).customer = orgObject['customer'] as { id: number; businessName: string; spatialTrackingEnabled?: boolean; };
            (currentUser as IUser).organization = orgObject['organization'] as { id: string; businessName: string; };
            (currentUser as IUser).building = { id: orgObject['building'].id, name: orgObject['building'].businessName } as { id: string; name: string; };
            this.requestsService.saveCurrentUser(currentUser);
            this.currentUser = currentUser;
            this.updateOrganizationResetButtonState();
            this.clearAlertsHistoryTableFilters();
            this.sharedService.clearAlerts();
            this.sharedService.startWss(this.toastService);
          }
          break;
        case 'building':
          // Qua ci entra solo un account infermiera o comunque NON SUPERUSER
          if (currentUser) {
            const selectedBuilding = setting.inputs[0].values.find((value) => value.id === setting.inputs[0].selectedValue);
            this.sharedService.stopWss();
            (currentUser as IUser).building = {
              id: selectedBuilding?.id || '',
              name: selectedBuilding?.label || ''
            };
            this.requestsService.saveCurrentUser(currentUser);
            this.clearAlertsHistoryTableFilters();
            this.sharedService.clearAlerts();
            this.sharedService.startWss(this.toastService);
          }
          break;
        default:
          break;
      }
    });
    this.notificationService.checkNotificationPermission();
    this.toastService.showToast(this.translate.instant('GENERAL.SUCCESS.CHANGES_SUCCESSFULLY'), IonicColors.GREEN)
  }

  clearAlertsHistoryTableFilters() {
    this.tableService.clearTableFilters('alertsHistory', ['room', 'unit']);
  }

  get tableRowSpan(): number {
    return this.settingsData.reduce((prev, curr) => prev + curr.rowSpan, 0) + 1
  }

}

export interface ISettingsData {
  id: string,
  label: string,
  rowSpan: number,
  inputs: {
    id: string,
    type: 'select' | 'status' | 'button',
    length: number,
    label: string,
    selectedValue?: string | null,
    disableTranslation?: boolean,
    touched?: boolean,
    disabled?: boolean,
    fill?: string,
    color?: string,
    values: {
      id: string,
      label: string,
      status?: boolean,
      spatialTrackingEnabled?: boolean
    }[],
    onSelect?: (resourceId: string | number) => void;
  }[]
}
